import { AxiosError } from "axios";
import { StatusLocatario, TipoVeiculo } from "./constantes";
import { ApiError, DiaDaSemana, FiltroDataPagamento, Locacao, StatusLocacao, StatusPagamento, StatusTrocaDeOleo, StatusVistoria, TipoLancamento, TipoPeriodicidadePagamento, TipoStatusLancamento, TipoStatusMulta, TpDesconto, TpEscolhaRelatorioVeiculosSituacao } from "./tipos";
import { toast } from "react-toastify";
import { ToWords } from 'to-words';

export function obterVersao(): string {
    return 'v9.7.1';
}

export function diasRestantes(data: string | undefined): number {
    const dataExpiracao = parseDataBRParaDate(data || '');
    if (!dataExpiracao) return Infinity;
    const hoje = new Date();
    const diffMs = dataExpiracao.getTime() - hoje.getTime();
    return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
}

export function parseDataBRParaDate(dataHoraBR: string) {
    const [data, hora] = dataHoraBR.split(' ');
    const [dia, mes, ano] = data.split('/');
    return new Date(`${ano}-${mes}-${dia}T${hora}`);
}

export function calcularTempoRestante(dataFinal: Date): string {
    const agora = new Date();
    const diffMs = dataFinal.getTime() - agora.getTime();

    if (diffMs <= 0) {
        return 'Expirado';
    }

    const diffHoras = diffMs / (1000 * 60 * 60);
    const diffDias = diffHoras / 24;
    const diffMeses = diffDias / 30;

    if (diffMeses >= 1) {
        const meses = Math.floor(diffMeses);
        return `${meses} ${meses > 1 ? 'meses' : 'mês'}`;
    } else if (diffDias >= 1) {
        const dias = Math.floor(diffDias);
        return `${dias} dia${dias > 1 ? 's' : ''}`;
    } else {
        const horas = Math.floor(diffHoras);
        return `${horas} hora${horas > 1 ? 's' : ''}`;
    }
}

export function obterDataCadastro(dataString?: string | null): Date | null {
    if (!dataString || dataString.trim() === '') {
        return null;
    }

    try {
        return parseDataBRParaDate(dataString);
    } catch (error) {
        console.error("Erro ao processar data de cadastro:", error);
        return null;
    }
}

export function extrairMensagemErroApi(error: AxiosError<ApiError>): string {
    const data: any = error.response?.data;
    if (typeof data === 'string' && data.trim()) return data;
    if (data?.Mensagem) return data.Mensagem;
    if (data?.mensagem) return data.mensagem;
    if (data?.message) return data.message;
    if (data?.errors) {
        const raw: any[] = Array.isArray(data.errors) ? data.errors : (Object.values(data.errors) as any[]);
        const flat: string[] = raw.reduce<string[]>((acc, v) => acc.concat(v), []);
        const msg = flat.filter(Boolean).join(', ');
        if (msg) return msg;
        return 'Erro de validação';
    }
    return error.message || 'Erro desconhecido';
}

export function tratarErroApi(error: AxiosError<ApiError>, mensagem: string): string[] {
    let mensagens: string[] = [];
    mensagens.push(mensagem);

    if (error.response?.data.Mensagem && error.response?.data.Mensagem.includes('duplicar valor da chave viola a restrição de unicidade')) {
        mensagens.push('Já existe um registro criado para o valor informado.');
        mensagens.push('Verifique: Peça, Locatário, Veículo');
    } else {
        if (error.response !== undefined) {
            if (error.response.data !== undefined) {
                if (error.response.data.title !== undefined) {
                    if (error.response.data.title === 'One or more validation errors occurred.') {
                        mensagens.push('Ocorreu um ou mais erros de validação.');
                    } else {
                        mensagens.push(error.response.data.title);
                    }
                }
                if (error.response.data.errors !== undefined) {
                    const errors = error.response.data.errors;
                    if (Array.isArray(errors)) {
                        errors.forEach(e => mensagens.push(e));
                    } else {
                        for (const campo in errors) {
                            const errosCampo = (errors as Record<string, string[]>)[campo];
                            if (Array.isArray(errosCampo)) {
                                errosCampo.forEach(e => mensagens.push(e));
                            } else {
                                mensagens.push(String(errosCampo));
                            }
                        }
                    }
                }
                if (error.response.data.Mensagem !== undefined) {
                    mensagens.push(error.response.data.Mensagem);
                }
            }
        }
    }

    return mensagens;
}

export function converterStatusLocatarioParaTexto(status: Number) {
    switch (status) {
        case StatusLocatario.stAtivo:
            return "Ativo";
        case StatusLocatario.stInativo:
            return "Inativo";
        case StatusLocatario.stEmLocacao:
            return "Em Locação";
        case StatusLocatario.stHabilitacaoVencida:
            return "Habilitação Vencida";
    }
}

export function converterStatusLocacaoParaTexto(status: Number) {
    switch (status) {
        case StatusLocacao.Ativa:
            return "Ativa";
        case StatusLocacao.Inativa:
            return "Inativa";
        case StatusLocacao.Encerrada:
            return "Encerrada";
        case StatusLocacao.Nenhum:
            return "Todos";
    }
}

export function converterPeriodicidadePagamentoParaTexto(periodicidade: Number) {
    switch (periodicidade) {
        case TipoPeriodicidadePagamento.Semanal:
            return "Semanal";
        case TipoPeriodicidadePagamento.Quinzenal:
            return "Quinzenal";
        case TipoPeriodicidadePagamento.Mensal:
            return "Mensal";
        case TipoPeriodicidadePagamento.Nenhum:
            return "Nenhum";
        case TipoPeriodicidadePagamento.Diario:
            return "Diário";
    }
}

export function converterPeriodicidadeTempoParaTexto(periodicidade: Number) {
    switch (periodicidade) {
        case TipoPeriodicidadePagamento.Semanal:
            return "Semana(s)";
        case TipoPeriodicidadePagamento.Quinzenal:
            return "Quinzena(s)";
        case TipoPeriodicidadePagamento.Mensal:
            return "Mês(es)";
        case TipoPeriodicidadePagamento.Nenhum:
            return "Nenhum";
        case TipoPeriodicidadePagamento.Diario:
            return "Dia(s)";
    }
}


export function converterTipoLancamentoParaTexto(tipoLancamento: TipoLancamento) {
    switch (tipoLancamento) {
        case TipoLancamento.Entrada:
            return "Entrada (Receita)";
        case TipoLancamento.Saida:
            return "Saída (Despesa)";
        case TipoLancamento.Nenhum:
            return "Nenhum";
        case TipoLancamento.Todos:
            return "Todos";
    }
}

export function converterTipoStatusLancamentoParaTexto(tipoStatusLancamento: TipoStatusLancamento) {
    switch (tipoStatusLancamento) {
        case TipoStatusLancamento.Resolvido:
            return "Resolvido";
        case TipoStatusLancamento.Pendente:
            return "A Vencer";
        case TipoStatusLancamento.Nenhum:
            return "Nenhum";
        case TipoStatusLancamento.Todos:
            return "Todos";
    }
}

export function converterTipoStatusMultaParaTexto(tipoStatusMulta: TipoStatusMulta) {
    switch (tipoStatusMulta) {
        case TipoStatusMulta.Paga:
            return "Paga";
        case TipoStatusMulta.Vencida:
            return "Vencida";
        case TipoStatusMulta.EmAberto:
            return "A Vencer";
        case TipoStatusMulta.Cancelada:
            return "Cancelada";
        case TipoStatusMulta.Nenhum:
            return "Nenhum";
        case TipoStatusMulta.Todos:
            return "Todos";
    }
}

export function converterDiaSemanaParaTexto(dia: DiaDaSemana) {
    switch (dia) {
        case DiaDaSemana.domingo:
            return "DOMINGO";
        case DiaDaSemana.segundaFeira:
            return "SEGUNDA-FEIRA";
        case DiaDaSemana.tercaFeira:
            return "TERÇA-FEIRA";
        case DiaDaSemana.quartaFeira:
            return "QUARTA-FEIRA";
        case DiaDaSemana.quintaFeira:
            return "QUINTA-FEIRA";
        case DiaDaSemana.sextaFeira:
            return "SEXTA-FEIRA";
        case DiaDaSemana.sabado:
            return "SÁBADO";
    }
}

export function converterStatusPagamentoParaTexto(statusPagamento: StatusPagamento) {
    switch (statusPagamento) {
        case StatusPagamento.Todos:
            return "Todos";
        case StatusPagamento.Pago:
            return "Pago";
        case StatusPagamento.Pendente:
            return "A Vencer";
        case StatusPagamento.EmAtraso:
            return "Em atraso";
    }
}

export function converterStatusTrocaDeOleoParaTexto(statusTrocaDeOleo: StatusTrocaDeOleo) {
    switch (statusTrocaDeOleo) {
        case StatusTrocaDeOleo.Aprovada:
            return "Aprovada";
        case StatusTrocaDeOleo.Recusada:
            return "Recusada";
        case StatusTrocaDeOleo.EmAnalise:
            return "Aguardando Analise";
        case StatusTrocaDeOleo.FeitoPeloLocador:
            return "Criado por Locador";
    }
}

export function converterTipoVeiculoParaTexto(tipo: number, plural = false): string {
    switch (tipo) {
        case TipoVeiculo.tpCarro:
            return plural ? "Carros" : "Carro";
        case TipoVeiculo.tpMoto:
            return plural ? "Motos" : "Moto";
        case TipoVeiculo.tpCaminhao:
            return plural ? "Caminhões" : "Caminhão";
        case TipoVeiculo.tpAmbos:
            return plural ? "Veículos" : "Veículo";
        default:
            return plural ? "Outros" : "Outro";
    }
}

export function converterStatusVistoriaParaTexto(statusVistoria: StatusVistoria) {
    switch (statusVistoria) {
        case StatusVistoria.Aprovada:
            return "Aprovada";
        case StatusVistoria.Recusada:
            return "Recusada";
        case StatusVistoria.EmAnalise:
            return "Aguardando Analise";
        case StatusVistoria.FeitoPeloLocador:
            return "Criado por Locador";
    }
}

export const converterFiltroDataPagamentoParaTexto = (filtro: FiltroDataPagamento): string => {
    switch (filtro) {
        case FiltroDataPagamento.Hoje:
            return "Hoje";
        case FiltroDataPagamento.Semana:
            return "Esta semana";
        case FiltroDataPagamento.Quinzena:
            return "Quinzena";
        case FiltroDataPagamento.Mes:
            return "Este mês";
        case FiltroDataPagamento.Personalizado:
            return "Personalizado";
        case FiltroDataPagamento.Todos:
            return "Todos";
        default:
            return "Personalizado";
    }
};


export function obterNumeroDoDia(data: Date): number {
    const dia = data.getDay();

    switch (dia) {
        case 0:
            return DiaDaSemana.segundaFeira;
        case 1:
            return DiaDaSemana.tercaFeira;
        case 2:
            return DiaDaSemana.quartaFeira;
        case 3:
            return DiaDaSemana.quintaFeira;
        case 4:
            return DiaDaSemana.sextaFeira;
        case 5:
            return DiaDaSemana.sabado;
        case 6:
            return DiaDaSemana.domingo;
        default:
            return DiaDaSemana.nenhum;
    }
}

export enum PeriodicidadeDashboard {
    semanaAtual = 0,
    mesAtual = 1,
    ultimosQuinzeDias = 2,
    ultimosTrintaDias = 3,
    ultimoAno = 4,
    personalizado = 5,
}

export function parseDateFromString(dateStr: string): Date {
    // Aceita "YYYY-MM-DD" e "YYYY-MM-DDTHH:mm[:ss]": só o dia interessa aqui, em hora local.
    const [year, month, day] = String(dateStr).substring(0, 10).split('-').map(Number);
    return new Date(year, month - 1, day);
}

/**
 * Data com hora quando ela existe ("29/10/2026 14:30"), só a data quando é meia-noite ("29/10/2026"):
 * locações antigas foram gravadas sem hora e não devem aparecer como "00:00".
 */
export const formatarDataHora = (valor?: string | Date | null): string => {
    if (!valor) return '';
    const data = valor instanceof Date ? valor : new Date(valor);
    if (isNaN(data.getTime())) return '';
    const dia = data.toLocaleDateString('pt-BR');
    const hora = data.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', hour12: false });
    return hora === '00:00' ? dia : `${dia} ${hora}`;
};

export const formatarData = (data?: Date) => {
    if (data instanceof Date && !isNaN(data.getTime())) {
        return new Date(data.getFullYear(), data.getMonth(), data.getDate()).toLocaleDateString("pt-BR", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric"
        });
    } else {
        return "";
    }
};

export function formatDate(dateString: string) {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
}

export const IdEmpresaAdm = 'f5ec617c-48b1-4e42-a3e9-c545c974f31a';

/**
 * Dia de uma data, em hora local, no formato que os campos de data e a API esperam ("YYYY-MM-DD").
 * Antes devolvia "YYYY-MM-DDT00:00:00Z": com o conversor de datas da API, o "Z" é tratado como instante
 * absoluto e vira 21:00 do dia anterior em Brasília, recuando um dia o período dos relatórios.
 */
export const formatDateToISOWithZeroTime = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
};

export function formatarParaReais(valor?: number | string | null): string {
    const numero = Number(valor);

    if (!Number.isFinite(numero)) {
        return 'R$ 0,00';
    }

    return numero.toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL'
    });
}

export function ObterProximaDataDePagamento(dataInicio: Date, periodicidade: TipoPeriodicidadePagamento): Date {
    const proximaData = new Date(dataInicio);

    switch (periodicidade) {
        case TipoPeriodicidadePagamento.Diario:
            proximaData.setDate(proximaData.getDate() + 1);
            break;
        case TipoPeriodicidadePagamento.Semanal:
            proximaData.setDate(proximaData.getDate() + 7);
            break;
        case TipoPeriodicidadePagamento.Quinzenal:
            proximaData.setDate(proximaData.getDate() + 15);
            break;
        case TipoPeriodicidadePagamento.Mensal:
            proximaData.setMonth(proximaData.getMonth() + 1);
            break;
    }

    return proximaData;
}

export function ObterTipoPeriodicidadePagamento(valor: number): TipoPeriodicidadePagamento {
    if (valor === 0) {
        return TipoPeriodicidadePagamento.Nenhum;
    } else if (valor === 1) {
        return TipoPeriodicidadePagamento.Semanal;
    } else if (valor === 2) {
        return TipoPeriodicidadePagamento.Quinzenal;
    } else if (valor === 3) {
        return TipoPeriodicidadePagamento.Mensal;
    } else if (valor === 4) {
        return TipoPeriodicidadePagamento.Diario;
    } else {
        return TipoPeriodicidadePagamento.Nenhum;
    }
}

export function calcularDiferencaDias(diaInicial: DiaDaSemana, diaFinal: DiaDaSemana): number {
    if (diaInicial === DiaDaSemana.nenhum || diaFinal === DiaDaSemana.nenhum) {
        return 0;
    }

    let diferenca = diaFinal - diaInicial;

    if (diferenca <= 0) {
        diferenca += 7;
    }

    return diferenca;
}

export function calcularDiaria(
    tipo: TipoPeriodicidadePagamento,
    valor: number,
    dataBase: Date
): number {
    switch (tipo) {
        case TipoPeriodicidadePagamento.Semanal:
            return valor / 7;

        case TipoPeriodicidadePagamento.Quinzenal:
            return valor / 15;

        case TipoPeriodicidadePagamento.Mensal:
            const ano = dataBase.getFullYear();
            const mes = dataBase.getMonth();
            const diasDoMes = new Date(ano, mes + 1, 0).getDate();
            return valor / diasDoMes;

        case TipoPeriodicidadePagamento.Diario:
            return valor;

        case TipoPeriodicidadePagamento.Nenhum:
        default:
            return 0;
    }
}

export function ConverterEscolhaRelatorioVeiculosSituacaoParaTexto(escolha: TpEscolhaRelatorioVeiculosSituacao) {
    switch (escolha) {
        case TpEscolhaRelatorioVeiculosSituacao.Ativo: return "Em locação";
        case TpEscolhaRelatorioVeiculosSituacao.Parado: return "Parado";
        case TpEscolhaRelatorioVeiculosSituacao.Vendido: return "Vendido";
        case TpEscolhaRelatorioVeiculosSituacao.Roubado: return "Roubado";
        case TpEscolhaRelatorioVeiculosSituacao.Inativo: return "Inativo";
        default: return "Todos";
    }
}

export function ConverterTipoDescontoParaTexto(tpDesconto: TpDesconto) {
    if (tpDesconto == TpDesconto.Percentual) {
        return "Percentual"
    } else if (tpDesconto == TpDesconto.ValorFixo) {
        return "Valor Fixo"
    } else {
        return "Nenhum"
    }
}

export function calcularDataTermino(locacao: Locacao): Date {
    const { dataInicio, dataTermino } = locacao;

    if (dataTermino) {
        return new Date(dataTermino);
    }

    const inicio = new Date(dataInicio);

    return new Date(inicio.setMonth(inicio.getMonth() + 72));
}

export const handleDownloadFile = async (url: string, filename: string, urlNaoExiste?: () => void, success?: () => void, error?: () => void) => {
    try {
        if (!url) {
            urlNaoExiste && urlNaoExiste();
            return;
        }

        const response = await fetch(url);

        if (!response.ok) {
            error && error();
            return;
        }

        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        URL.revokeObjectURL(blobUrl);

        setTimeout(() => {
            success && success();
        }, 500)
    } catch (error) {
        toast.error('Ocorreu um erro durante esse processo de download de arquivo')
    }
};

export const getWeekRange = (currentDate: Date): { startOfWeek: Date; endOfWeek: Date } => {
    const dayOfWeek = currentDate.getDay();
    const startOfWeek = new Date(currentDate);
    const diffToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    startOfWeek.setDate(currentDate.getDate() + diffToMonday);
    startOfWeek.setHours(0, 0, 0, 0);

    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);
    endOfWeek.setHours(23, 59, 59, 999);

    return { startOfWeek, endOfWeek };
};

export const formatTimeAgo = (date?: Date | string) => {
    if (!date) return '';
    const now = new Date();
    const d = typeof date === 'string' ? new Date(date) : date;
    const diffMs = d.getTime() - now.getTime();
    const isFuture = diffMs > 0;

    const diffSec = Math.floor(Math.abs(diffMs) / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHrs = Math.floor(diffMin / 60);
    const diffDays = Math.floor(diffHrs / 24);

    const prefix = isFuture ? 'Em' : 'Há';

    if (diffSec < 60) {
        return `${prefix} ${diffSec} segundo${diffSec !== 1 ? 's' : ''}`;
    }

    if (diffMin < 60) {
        return `${prefix} ${diffMin} minuto${diffMin !== 1 ? 's' : ''}`;
    }

    if (diffHrs < 24) {
        const horas = diffHrs;
        const minutos = diffMin % 60;
        return `${prefix} ${horas} hora${horas !== 1 ? 's' : ''}`
            + (minutos > 0 ? ` e ${minutos} minuto${minutos !== 1 ? 's' : ''}` : '');
    }

    if (diffDays < 30) {
        return `${prefix} ${diffDays} dia${diffDays !== 1 ? 's' : ''}`;
    }

    const diffMonths = Math.floor(diffDays / 30);
    if (diffMonths < 12) {
        return `${prefix} ${diffMonths} ${diffMonths !== 1 ? 'meses' : 'mês'}`;
    }

    const diffYears = Math.floor(diffMonths / 12);
    return `${prefix} ${diffYears} ano${diffYears !== 1 ? 's' : ''}`;
};

type Endereco = {
    cep?: string;
    logradouro?: string;
    numero?: string;
    estado?: string;
    cidade?: string;
    bairro?: string;
    complemento?: string;
};

export function formatarEndereco({
    cep,
    logradouro,
    numero,
    estado,
    cidade,
    bairro,
    complemento,
}: Endereco): string {
    let endereco = "";

    if (logradouro) endereco += logradouro;
    if (numero) endereco += `, ${numero}`;
    if (complemento) endereco += ` - ${complemento}`;
    if (bairro) endereco += ` - ${bairro}`;
    if (cidade) endereco += `, ${cidade}`;
    if (estado) endereco += ` - ${estado}`;
    if (cep) endereco += `, CEP: ${cep}`;

    return endereco.trim();
}

const toWords = new ToWords({
    localeCode: 'pt-BR',
    converterOptions: {
        currency: true,
        ignoreDecimal: false,
        ignoreZeroCurrency: false,
        doNotAddOnly: false,
        currencyOptions: {
            // can be used to override defaults for the selected locale
            name: 'Real',
            plural: 'Reais',
            symbol: 'R$',
            fractionalUnit: {
                name: 'Centavo',
                plural: 'Centavos',
                symbol: '',
            },
        },
    },
});

export function retornarDinheiroPorExtenso(numero: number): string {
    if (numero === 0) return 'zero reais';
    return `${formatarParaReais(numero)} (${toWords.convert(numero)})`;
}

export function corMaisClara(hex: string, percentual: number) {
    hex = hex.replace(/^#/, '');

    const num = parseInt(hex, 16);
    let r = (num >> 16) & 0xFF;
    let g = (num >> 8) & 0xFF;
    let b = num & 0xFF;

    r = Math.min(255, Math.floor(r + (255 - r) * percentual / 100));
    g = Math.min(255, Math.floor(g + (255 - g) * percentual / 100));
    b = Math.min(255, Math.floor(b + (255 - b) * percentual / 100));

    return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
}

export const dateToInputValue = (date: Date | null) => {
    if (!date) return "";
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

export const dataInputCompleta = (value: string): boolean =>
    /^\d{4}-\d{2}-\d{2}$/.test(value) && Number(value.slice(0, 4)) >= 1000;

export const inputValueToDate = (value: string): Date | null => {
    if (!dataInputCompleta(value)) return null;
    const [year, month, day] = value.split("-").map(Number);
    return new Date(year, month - 1, day, 12, 0, 0);
};

// Compara apenas o dia (ignora horas/fuso): datas de filtro chegam ora à meia-noite
// local, ora à meia-noite UTC — comparar os Date diretamente rejeitava período de um dia só.
export const diaInicialDepoisDoFinal = (inicial: Date, final: Date): boolean => {
    const ini = new Date(inicial.getFullYear(), inicial.getMonth(), inicial.getDate());
    const fim = new Date(final.getFullYear(), final.getMonth(), final.getDate());
    return ini.getTime() > fim.getTime();
};

export function parseDecimal(value: any): number {
    if (typeof value === 'number') return value;
    if (typeof value === 'string') {
        // Substitui vírgula por ponto
        const normalized = value.replace(',', '.');
        const number = parseFloat(normalized);
        return isNaN(number) ? 0 : number;
    }
    return 0;
}
